import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import { setFilter as setFilterAction, getSearchedTickets, resetFilter } from "../../../../actions/projectmail";
import { initialSearchFilter } from "../../../../reducers/projectmail";
import { defaultPasscodeTheme } from "../../../../constants/passCodeUtils";

const type = "closed";

const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  filterContainer: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    width: "100%",
    display: "inline-flex",
  },
  spaceButtons: {
    flex: "1",
    width: "10px",
  },
}));

const CloseFilterComponent = forwardRef((props, ref) => {
  const {
    filter,
    setFilterFn,
    requestFn,
    resetFilterFn,
    openFilter,
    translate,
    resetFn,
    inProgress,
    setInProgress,
    closeFilter,
  } = props;
  const classes = useStyles();
  const [eventListener, setEventListener] = useState(undefined);
  useImperativeHandle(ref, () => ({
    setFilter: (filterValue, filterKey) => {
      setFilterFn({ ...filter, [filterKey]: filterValue, filterUsed: true });
    },
  }));

  useEffect(() => {
    const enterEvent = async e => {
      if (e.key === "Enter" && openFilter === true && !inProgress) {
        setInProgress(true);
        await requestFn(filter, type);
        setInProgress(false);
      }
    };
    if (eventListener === undefined) setEventListener(window.addEventListener("keydown", enterEvent));
    return function cleanup() {
      window.removeEventListener("keydown", enterEvent);
      setEventListener(undefined);
    };
  }, [eventListener, setEventListener, openFilter, inProgress, setInProgress, filter, requestFn]);

  useEffect(() => {
    if (filter === undefined) resetFilterFn(type);
  }, [filter, resetFilterFn]);

  const handleTextChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.value, filterUsed: true });
  };

  const textfield = (label, key) => (
    <TextField
      label={label}
      className={`${classes.textField} ${classes.lineExpander}`}
      value={filter ? filter[key] : ""}
      onChange={handleTextChange(key)}
    />
  );

  return (
    <div className={classes.filterContainer}>
      {textfield(translate.title, "title")}
      {/** TODO: change id to externalId */}
      {textfield(translate.externalId, "externalId")}
      <div className={classes.lineBreak} />
      {textfield(translate.customer, "customer")}
      {textfield(translate.EmailTicketSystemOwner, "owner")}
      <div className={classes.lineBreak} />
      <div className={classes.lineBreak} />
      {textfield(translate.orderID, "orderID")}
      <Divider />
      <div className={classes.buttonContainer}>
        <div className={classes.spaceButtons} />
        <Button
          size="small"
          onClick={async () => {
            resetFn();
            resetFilterFn(type);
            setInProgress(true);
            await requestFn(initialSearchFilter, type);
            closeFilter();
            setInProgress(false);
          }}
          disabled={inProgress}
        >
          Reset
        </Button>
        <Button
          disabled={inProgress}
          variant="contained"
          size="small"
          color="primary"
          onClick={async () => {
            setInProgress(true);
            await requestFn(filter, type);
            closeFilter();
            setInProgress(false);
          }}
        >
          Suche
        </Button>
      </div>
    </div>
  );
});

CloseFilterComponent.defaultProps = {
  filter: undefined,
};

CloseFilterComponent.propTypes = {
  filter: PropTypes.object,
  setFilterFn: PropTypes.func.isRequired,
  requestFn: PropTypes.func.isRequired,
  resetFilterFn: PropTypes.func.isRequired,
  openFilter: PropTypes.bool.isRequired,
  translate: PropTypes.object.isRequired,
  resetFn: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
  setInProgress: PropTypes.func.isRequired,
  closeFilter: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filter: state.projectmail.searchFilter[type],
  translate: state.ui.translate,
});

const mapDispatchToProps = {
  setFilterFn: filter => dispatch => dispatch(setFilterAction(filter, type)),
  requestFn: filter => async dispatch => {
    await dispatch(getSearchedTickets(filter, type));
  },
  resetFilterFn: () => dispatch => dispatch(resetFilter(type)),
};

const CloseFilter = connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
)(CloseFilterComponent);

export default CloseFilter;
